import {
  ReplacedBy,
  SaleStatus,
  TransactionType,
} from "../../types/sale.type";

export const SALES_STATUS_COLOR: Record<SaleStatus, string> = {
  pending: "#FDA42C",
  approve: "#45BE77",
  paid: "#45BE77",
  cancel: "#FB3B3B",
  pending_cancel: "#FB3B3B",
};

export const SALES_STATUS_LABEL: Record<SaleStatus, string> = {
  pending: "รอตรวจสอบ",
  approve: "ยอมรับแล้ว",
  paid: "จ่ายแล้ว",
  cancel: "ยกเลิก",
  pending_cancel: "ขอยกเลิกบิล",
};

/** The ทั้งหมด option of the list's select filters: no filtering on that field. */
export const ALL_OPTION = "all";

/** Rows-per-page choices of the list; the first is where the list opens. */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

export const STATUS_FILTER = [
  { label: "ทั้งหมด", value: ALL_OPTION },
  { label: "รอตรวจสอบ", value: "pending" },
  { label: "ยอมรับแล้ว", value: "approve" },
  { label: "จ่ายแล้ว", value: "paid" },
  { label: "ขอยกเลิกบิล", value: "pending_cancel" },
  { label: "ยกเลิก", value: "cancel" },
];

export const SALES_TABS = [
  { label: "ใบส่งสินค้า/ใบแจ้งหนี้", value: "invoice" },
  { label: "ขายเงินสด", value: "cashSales" },
];

export const DETAILS_TABS = [
  { label: "ข้อมูลเอกสาร", value: "ข้อมูลเอกสาร" },
];

export const TRANSACTION_TYPE_LABEL: Record<TransactionType, string> = {
  invoice: "เครดิต",
  cashsale: "ขายสด",
};

// Colors swapped between cash sale and credit per design.
// Previously: invoice (credit) = green, cashsale = blue.
export const TRANSACTION_TYPE_COLOR: Record<TransactionType, string> = {
  invoice: "#5C69E8",
  cashsale: "#45BE77",
};

/**
 * payment_type -> Thai channel label. Same four values and the same wording as the
 * sale report's `PAYMENT_CHANNEL_LABELS` (Report/MoreDetails/dailyGrid.utils.ts), so a
 * bill reads identically on both screens.
 */
export const PAYMENT_CHANNEL_LABELS: Record<string, string> = {
  QR_CODE: "QR Code",
  CASH: "เงินสด",
  BANK_TRANSFER: "โอนเงิน",
  CREDIT: "เครดิต",
};

/** The channels a user may pick when correcting a bill. Mirrors `payment_type_enum`. */
export const PAYMENT_CHANNEL_OPTIONS = [
  { label: PAYMENT_CHANNEL_LABELS.QR_CODE, value: "QR_CODE" },
  { label: PAYMENT_CHANNEL_LABELS.CASH, value: "CASH" },
  { label: PAYMENT_CHANNEL_LABELS.BANK_TRANSFER, value: "BANK_TRANSFER" },
  { label: PAYMENT_CHANNEL_LABELS.CREDIT, value: "CREDIT" },
] as const;

/**
 * Channels the list may be filtered by.
 *
 * `CREDIT` is absent: a credit sale is an invoice, which the ประเภท column and the tab
 * already say, so filtering by it would only duplicate a control that exists.
 */
export const PAYMENT_CHANNEL_FILTER = [
  { label: "ทั้งหมด", value: ALL_OPTION },
  { label: PAYMENT_CHANNEL_LABELS.CASH, value: "CASH" },
  { label: PAYMENT_CHANNEL_LABELS.BANK_TRANSFER, value: "BANK_TRANSFER" },
  { label: PAYMENT_CHANNEL_LABELS.QR_CODE, value: "QR_CODE" },
] as const;

/** Settlement statuses this screen may set. Cancelling a bill has its own flow. */
export const PAYMENT_STATUS_OPTIONS = [
  { label: "จ่ายแล้ว", value: "paid" },
  { label: "ค้างจ่าย", value: "pending" },
] as const;

/**
 * Render a payment channel for display.
 *
 * Three outcomes, deliberately distinct — the same rule the sale report's channel
 * column settled on in #303:
 *
 * - a known channel → its Thai label
 * - **absent** (legacy bills predating payment_type) → `-`
 * - **present but unrecognised** (a new channel shipped after this build) → the RAW
 *   value. Collapsing it into `-` would make it indistinguishable from a legacy bill,
 *   so the day a new channel ships every bill using it would silently read as blank.
 */
export const getPaymentChannelLabel = (paymentType?: string | null): string => {
  if (!paymentType) return "-";
  return PAYMENT_CHANNEL_LABELS[paymentType.toUpperCase()] ?? paymentType;
};

export const getStatusLabel = (status: SaleStatus): string =>
  SALES_STATUS_LABEL[status] ?? "-";

export const getStatusColor = (status: SaleStatus): string =>
  SALES_STATUS_COLOR[status] ?? "#6a6a6a";

/**
 * Replacement-bill GPS flag -> chip colour. Red is the one that matters: the
 * salesperson stood nearer another shop billed on the route today, the signature of
 * replacing the wrong shop's bill. OK (and a missing flag) render no chip at all.
 */
export const REPLACE_FLAG_COLOR = {
  NEAR_OTHER_SHOP: "#FB3B3B",
  FAR: "#FDA42C",
  NO_GPS: "#6a6a6a",
  NO_PIN: "#6a6a6a",
} as const;

export interface ReplaceFlagChip {
  label: string;
  color: string;
}

/** Whole metres for display; `null` when the backend had no distance to give. */
const roundMetres = (value: number | null | undefined): number | null =>
  value == null || !Number.isFinite(Number(value))
    ? null
    : Math.round(Number(value));

/**
 * The chip for a replacement bill's GPS flag, or `null` for OK / no flag / no
 * replacement — the backend may predate the field entirely.
 */
export const getReplaceFlagChip = (
  replacedBy?: ReplacedBy | null,
): ReplaceFlagChip | null => {
  switch (replacedBy?.replace_gps_flag) {
    case "NEAR_OTHER_SHOP":
      return { label: "ใกล้ร้านอื่น", color: REPLACE_FLAG_COLOR.NEAR_OTHER_SHOP };
    case "FAR": {
      const metres = roundMetres(replacedBy.replace_distance_m);
      return {
        label: metres == null ? "ห่างร้าน" : `ห่างร้าน ${metres} ม.`,
        color: REPLACE_FLAG_COLOR.FAR,
      };
    }
    case "NO_GPS":
      return { label: "ไม่มี GPS", color: REPLACE_FLAG_COLOR.NO_GPS };
    case "NO_PIN":
      return { label: "ร้านไม่มีพิกัด", color: REPLACE_FLAG_COLOR.NO_PIN };
    default:
      return null;
  }
};

/** "แทนด้วย {เลขบิล} · {ร้าน}" — the shop falls back from name to code. */
export const getReplacedByLabel = (replacedBy: ReplacedBy): string => {
  const shop = replacedBy.customer_name || replacedBy.customer_code;
  return [`แทนด้วย ${replacedBy.transaction_no ?? "-"}`, shop]
    .filter(Boolean)
    .join(" · ");
};

/** Rounded metres for prose, "-" when unknown. */
export const formatReplaceMetres = (value: number | null | undefined): string =>
  roundMetres(value)?.toString() ?? "-";
